import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router";
import type { CompanySummary } from "@mvk/shared";
import {
  ArrowLeft,
  Users,
  Plus,
  Loader2,
  Search,
  QrCode,
  Barcode,
  Download,
  Copy,
  Check,
  Pencil,
  Trash2,
  X,
  CheckCircle2,
  CircleDashed,
} from "lucide-react";
import { useAdminEvent } from "../hooks/useAdminEvent";
import { AdminPagination } from "../components/AdminPagination";
import {
  adminEventsApi,
  type AccessAuthorizedFilter,
  type AccessRoleFilter,
  type GrantExhibitorAccessResponse,
  type VisitorAccessRow,
} from "../../services/adminEventsApi";
import { isApiEnabled } from "../../api/config";
import { ApiError } from "../../api/client";
import {
  downloadBarcodePng,
  downloadQrPng,
  generateBarcodeDataUrl,
  generateQrDataUrl,
} from "../../utils/codeImages";
import { adminAnalyticsApi } from "../../services/adminAnalyticsApi";
import type { VisitorAnalyticsDto } from "@mvk/shared";
import { PhoneInput } from "../../components/PhoneInput";
import { formatPhoneDisplay, normalizePhoneInput } from "../../utils/phoneFormat";

type GrantMode = "visitor" | "exhibitor" | "both";

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function roleLabel(role: "visitor" | "exhibitor") {
  return role === "exhibitor" ? "Представитель" : "Посетитель";
}

/** Access via sibling event in the same exhibition group (no ticket on this event). */
function isGroupOnlyAccess(v: VisitorAccessRow) {
  return v.source === "event_group" && !v.ticketNumber;
}

function VisitorAnalyticsPanel({ eventId, userId }: { eventId: string; userId: string }) {
  const [data, setData] = useState<VisitorAnalyticsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminAnalyticsApi.getVisitor(eventId, userId);
        if (!cancelled) setData(res.analytics);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Не удалось загрузить");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, userId]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-gray-400 py-2">
        <Loader2 size={14} className="animate-spin" /> Аналитика посетителя…
      </div>
    );
  }
  if (error || !data) {
    return <p className="text-[12px] text-gray-400 py-1">{error || "Нет данных"}</p>;
  }

  return (
    <div className="rounded-xl border border-indigo-100 bg-white p-3 space-y-3">
      <p className="text-[12px] font-medium text-indigo-900">Активность на выставке</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {[
          ["Сканы", data.scanCount],
          ["Просмотры стендов", data.companyViewCount],
          ["Товары", data.productViewCount],
          ["Поиски", data.searchCount],
          ["Лиды", data.leadCount],
        ].map(([label, value]) => (
          <div key={String(label)} className="rounded-lg bg-gray-50 px-2 py-2 text-center">
            <div className="text-[16px] text-gray-900">{value}</div>
            <div className="text-[10px] text-gray-500">{label}</div>
          </div>
        ))}
      </div>
      {data.lastActivityAt ? (
        <p className="text-[11px] text-gray-400">
          Последняя активность: {new Date(data.lastActivityAt).toLocaleString("ru-RU")}
        </p>
      ) : null}
      {data.recentScans.length > 0 ? (
        <div>
          <p className="text-[11px] text-gray-500 mb-1">Последние сканы</p>
          <ul className="space-y-1">
            {data.recentScans.slice(0, 5).map((s) => (
              <li key={s.id} className="text-[12px] text-gray-700 flex justify-between gap-2">
                <span className="truncate">{s.companyName || s.scanTargetType}</span>
                <span className="text-gray-400 whitespace-nowrap">
                  {new Date(s.scannedAt).toLocaleString("ru-RU", {
                    day: "2-digit",
                    month: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function VisitorCodes({ visitor }: { visitor: VisitorAccessRow }) {
  const payload = visitor.qrPayload || visitor.ticketNumber || "";
  const [qrUrl, setQrUrl] = useState<string>("");
  const [barcodeUrl, setBarcodeUrl] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const baseName = visitor.phone || visitor.fullName || visitor.userId;

  useEffect(() => {
    let cancelled = false;
    if (!payload) {
      setQrUrl("");
      setBarcodeUrl("");
      return;
    }
    void (async () => {
      try {
        const qr = await generateQrDataUrl(payload, 180);
        let barcode = "";
        try {
          barcode = generateBarcodeDataUrl(payload, 56);
        } catch {
          barcode = "";
        }
        if (!cancelled) {
          setQrUrl(qr);
          setBarcodeUrl(barcode);
        }
      } catch {
        if (!cancelled) {
          setQrUrl("");
          setBarcodeUrl("");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [payload]);

  if (!payload) {
    return (
      <p className="text-[12px] text-gray-400 px-1 py-2">
        Нет билета / баркода для этой выставки. Выдайте доступ заново или дождитесь синка
        тикетов.
      </p>
    );
  }

  return (
    <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-3 space-y-3">
      <div className="flex flex-wrap items-start gap-4">
        {qrUrl ? (
          <div className="flex flex-col items-center gap-1.5">
            <img src={qrUrl} alt="QR" className="w-[120px] h-[120px] rounded-lg bg-white border border-gray-100" />
            <span className="text-[10px] text-gray-400 flex items-center gap-1">
              <QrCode size={10} /> QR для входа
            </span>
          </div>
        ) : null}
        <div className="flex-1 min-w-[160px] space-y-2">
          <div>
            <div className="text-[11px] text-gray-400 mb-0.5">
              {visitor.barcodeOrigin === "generated"
                ? "Тестовый баркод (сгенерирован нами)"
                : "Баркод / номер билета из реплики Tezam"}
            </div>
            <code className="text-[12px] text-gray-800 break-all">{visitor.ticketNumber || payload}</code>
          </div>
          {barcodeUrl ? (
            <img src={barcodeUrl} alt="Barcode" className="w-full max-w-xs bg-white rounded border border-gray-100" />
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <button
              type="button"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await downloadQrPng(payload, `visitor-${baseName}`);
                } finally {
                  setBusy(false);
                }
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-indigo-50 text-indigo-600 text-[12px] hover:bg-indigo-100"
            >
              <Download size={12} /> QR PNG
            </button>
            {visitor.ticketNumber ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  try {
                    downloadBarcodePng(visitor.ticketNumber!, `visitor-${baseName}`);
                  } catch {
                    /* ignore */
                  }
                }}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-violet-50 text-violet-600 text-[12px] hover:bg-violet-100"
              >
                <Barcode size={12} /> Баркод PNG
              </button>
            ) : null}
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(payload);
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-600 text-[12px] hover:bg-gray-50"
            >
              {copied ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} />}
              {copied ? "Скопировано" : "Код"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AdminAccessPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { event, loading: eventLoading } = useAdminEvent(id);

  const [visitors, setVisitors] = useState<VisitorAccessRow[]>([]);
  const [total, setTotal] = useState(0);
  const [authorizedTotal, setAuthorizedTotal] = useState(0);
  const [onlineNow, setOnlineNow] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [roleFilter, setRoleFilter] = useState<AccessRoleFilter>("all");
  const [authFilter, setAuthFilter] = useState<AccessAuthorizedFilter>("all");
  const [exhibitors, setExhibitors] = useState<CompanySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [fullName, setFullName] = useState("");
  const [position, setPosition] = useState("");
  const [grantMode, setGrantMode] = useState<GrantMode>("visitor");
  const [companyId, setCompanyId] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [granting, setGranting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [portalCreds, setPortalCreds] = useState<GrantExhibitorAccessResponse | null>(null);
  const [copiedLogin, setCopiedLogin] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    fullName: "",
    position: "",
    phone: "",
    companyId: "",
  });
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const loadVisitors = useCallback(async () => {
    if (!id || !isApiEnabled()) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await adminEventsApi.listVisitorAccess(id, {
        q: debouncedSearch || undefined,
        role: roleFilter,
        authorized: authFilter,
        page,
        pageSize,
      });
      setVisitors(data.visitors);
      setTotal(data.total);
      setAuthorizedTotal(data.authorizedTotal ?? 0);
      setOnlineNow(data.onlineNow ?? 0);
    } catch {
      setError("Не удалось загрузить список доступов");
    } finally {
      setLoading(false);
    }
  }, [id, debouncedSearch, roleFilter, authFilter, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, roleFilter, authFilter]);

  const loadExhibitors = useCallback(async () => {
    if (!id || !isApiEnabled()) return;
    try {
      const data = await adminEventsApi.listExhibitors(id);
      setExhibitors(data.exhibitors);
      setCompanyId((prev) => prev || data.exhibitors[0]?.id || "");
    } catch {
      /* ignore — grant form will show empty select */
    }
  }, [id]);

  useEffect(() => {
    void loadVisitors();
  }, [loadVisitors]);

  useEffect(() => {
    void loadExhibitors();
  }, [loadExhibitors]);

  const needsCompany = grantMode === "exhibitor" || grantMode === "both";

  const handleGrant = async () => {
    setError("");
    setSuccess("");
    setPortalCreds(null);
    const normalized = normalizePhoneInput(phone);
    if (normalized.replace(/\D/g, "").length < 10) {
      setError("Введите корректный телефон");
      return;
    }
    if (needsCompany && !companyId) {
      setError("Выберите компанию экспонента");
      return;
    }
    if (needsCompany && fullName.trim().length < 2) {
      setError("Укажите ФИО представителя");
      return;
    }

    setGranting(true);
    try {
      if (grantMode === "visitor") {
        await adminEventsApi.grantVisitorAccess(id!, { phone: normalized });
        setSuccess("Доступ посетителя выдан, тестовый билет и баркод созданы");
      } else {
        const res = await adminEventsApi.grantExhibitorAccess(id!, {
          phone: normalized,
          companyId,
          fullName: fullName.trim(),
          position: position.trim() || undefined,
          alsoVisitor: true,
        });
        setPortalCreds(res.access);
        setSuccess(
          "Представитель создан: профиль, тестовый билет/баркод и доступ в приложение",
        );
        setFullName("");
        setPosition("");
      }
      setPhone("");
      await loadVisitors();
    } catch (err) {
      if (err instanceof ApiError) {
        setError("Не удалось выдать доступ");
      } else {
        setError("Ошибка сети");
      }
    } finally {
      setGranting(false);
    }
  };

  const startEdit = (v: VisitorAccessRow) => {
    setEditingId(v.userId);
    setExpandedId(v.userId);
    setEditForm({
      fullName: v.fullName || "",
      position: v.position || "",
      phone: v.phone || "",
      companyId: v.companyId || companyId || exhibitors[0]?.id || "",
    });
    setError("");
    setSuccess("");
  };

  const handleSaveEdit = async (userId: string) => {
    if (editForm.fullName.trim().length < 2) {
      setError("Укажите ФИО");
      return;
    }
    const normalized = normalizePhoneInput(editForm.phone);
    if (normalized.replace(/\D/g, "").length < 10) {
      setError("Введите корректный телефон");
      return;
    }
    setSavingEdit(true);
    setError("");
    try {
      await adminEventsApi.updateManualAccess(id!, userId, {
        fullName: editForm.fullName.trim(),
        position: editForm.position.trim(),
        phone: normalized,
        companyId: editForm.companyId || undefined,
      });
      setEditingId(null);
      setSuccess("Данные обновлены");
      await loadVisitors();
    } catch {
      setError("Не удалось сохранить изменения");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = async (v: VisitorAccessRow) => {
    const label = v.fullName || v.phone || v.userId;
    if (!window.confirm(`Удалить тестовый доступ «${label}» с этой выставки?`)) return;
    setDeletingId(v.userId);
    setError("");
    try {
      await adminEventsApi.deleteManualAccess(id!, v.userId);
      if (expandedId === v.userId) setExpandedId(null);
      if (editingId === v.userId) setEditingId(null);
      setSuccess("Тестовый доступ удалён");
      await loadVisitors();
    } catch {
      setError("Нельзя удалить: доступ не тестовый или ошибка сервера");
    } finally {
      setDeletingId(null);
    }
  };

  if (eventLoading) {
    return (
      <div className="p-6 flex justify-center text-gray-400">
        <Loader2 size={24} className="animate-spin" />
      </div>
    );
  }

  if (!event) {
    return <div className="p-6 text-gray-500">Мероприятие не найдено</div>;
  }

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(`/events/${id}`)}
          className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
          aria-label="Назад"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[18px] text-gray-900">Доступы</h1>
          <p className="text-[12px] text-gray-400 truncate">{event.title}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 space-y-3">
        <h2 className="text-[14px] text-gray-800 flex items-center gap-2">
          <Plus size={16} className="text-indigo-500" />
          Выдать доступ по телефону
        </h2>

        <div className="flex flex-wrap gap-1.5">
          {(
            [
              { id: "visitor" as const, label: "Посетитель" },
              { id: "exhibitor" as const, label: "Представитель" },
              { id: "both" as const, label: "Оба" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setGrantMode(opt.id)}
              className={`px-3 py-1.5 rounded-lg text-[12px] border transition-colors ${
                grantMode === opt.id
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {needsCompany ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">ФИО *</label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Иван Иванов"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
            <div>
              <label className="text-[11px] text-gray-400 mb-1 block">Должность</label>
              <input
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="Менеджер стенда"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
              />
            </div>
          </div>
        ) : null}

        {needsCompany ? (
          <div>
            <label className="text-[11px] text-gray-400 mb-1 block">Компания экспонента</label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 bg-white"
            >
              {exhibitors.length === 0 ? (
                <option value="">Нет компаний на выставке</option>
              ) : (
                exhibitors.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.booth ? ` · стенд ${c.booth}` : ""}
                  </option>
                ))
              )}
            </select>
          </div>
        ) : null}

        <div className="flex flex-col sm:flex-row gap-2">
          <PhoneInput
            value={phone}
            onChange={setPhone}
            className="flex-1 rounded-lg text-[13px]"
          />
          <button
            type="button"
            onClick={() => void handleGrant()}
            disabled={granting}
            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-[13px] flex items-center justify-center gap-2"
          >
            {granting ? <Loader2 size={14} className="animate-spin" /> : "Выдать"}
          </button>
        </div>

        {needsCompany ? (
          <p className="text-[11px] text-gray-400">
            Создаются профиль представителя, тестовый билет/баркод (не из реплики) и доступ в мини-приложение / PWA по
            телефону.
          </p>
        ) : null}

        {error && <p className="text-[12px] text-red-600">{error}</p>}
        {success && <p className="text-[12px] text-emerald-600">{success}</p>}

        {portalCreds ? (
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 space-y-2">
            <p className="text-[12px] text-amber-900 font-medium">Представитель готов</p>
            <div className="text-[12px] text-amber-900 space-y-1">
              <div>
                ФИО: <span className="font-medium">{portalCreds.fullName}</span>
                {portalCreds.position ? ` · ${portalCreds.position}` : ""}
              </div>
              <div>
                Компания: {portalCreds.companyName}
              </div>
              <div>
                Телефон (вход в приложение): <code className="font-mono">{portalCreds.phone}</code>
              </div>
              {portalCreds.ticketNumber ? (
                <div>
                  Билет / баркод:{" "}
                  <code className="font-mono">{portalCreds.ticketNumber}</code>
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(
                  [
                    portalCreds.fullName,
                    portalCreds.position,
                    portalCreds.phone,
                    portalCreds.ticketNumber,
                  ]
                    .filter(Boolean)
                    .join("\n"),
                );
                setCopiedLogin(true);
                setTimeout(() => setCopiedLogin(false), 1500);
              }}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-white border border-amber-200 text-amber-800 text-[12px]"
            >
              {copiedLogin ? <Check size={12} /> : <Copy size={12} />}
              {copiedLogin ? "Скопировано" : "Скопировать данные"}
            </button>
            <p className="text-[11px] text-amber-800/70">{portalCreds.loginHint}</p>
          </div>
        ) : null}

        {!isApiEnabled() && (
          <p className="text-[12px] text-amber-600">Требуется подключение к API</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-3 py-2.5 border-b border-gray-100 space-y-2">
          <div className="flex items-center gap-2">
            <Users size={16} className="text-sky-500 flex-shrink-0" />
            <span className="text-[13px] text-gray-800 flex-1 min-w-0 truncate">Доступы</span>
            <span
              className="text-[12px] text-gray-400 tabular-nums flex-shrink-0 text-right leading-snug"
              title="Всего доступов · когда-либо заходили в приложение · активность за последние 15 мин"
            >
              {total.toLocaleString("ru-RU")}
              {" · "}
              <span className="text-emerald-600">{authorizedTotal.toLocaleString("ru-RU")}</span>
              {" заходили"}
              {" · "}
              <span className="text-sky-600">{onlineNow.toLocaleString("ru-RU")}</span>
              {" сейчас"}
            </span>
          </div>
          <p className="text-[10px] text-gray-400 leading-snug px-0.5">
            «Заходили» — хотя бы раз открыли приложение. «Сейчас» — были активны последние 15 минут
            (не путать с онлайн в смысле WebSocket).
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 min-w-0 flex-1">
              <Search size={14} className="text-gray-400 flex-shrink-0" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Телефон, имя, email, компания, баркод…"
                className="flex-1 min-w-0 bg-transparent text-[13px] outline-none text-gray-800 placeholder:text-gray-400"
              />
            </div>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {(
                [
                  { id: "all" as const, label: "Все" },
                  { id: "visitor" as const, label: "Посетители" },
                  { id: "exhibitor" as const, label: "Представители" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setRoleFilter(opt.id)}
                  className={`px-2.5 py-1.5 text-[12px] ${
                    roleFilter === opt.id
                      ? "bg-indigo-600 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex rounded-lg border border-gray-200 overflow-hidden">
              {(
                [
                  { id: "all" as const, label: "Любой вход" },
                  { id: "yes" as const, label: "Заходили" },
                  { id: "no" as const, label: "Не входили" },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setAuthFilter(opt.id)}
                  className={`px-2.5 py-1.5 text-[12px] ${
                    authFilter === opt.id
                      ? "bg-emerald-700 text-white"
                      : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10 text-gray-400">
            <Loader2 size={24} className="animate-spin" />
          </div>
        ) : visitors.length === 0 ? (
          <p className="text-center text-[13px] text-gray-400 py-10">Никого не найдено</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left">
                <thead>
                  <tr className="border-b border-gray-100 text-[11px] text-gray-400 uppercase tracking-wider">
                    <th className="px-3 py-2 font-medium">ФИО</th>
                    <th className="px-3 py-2 font-medium">Роль</th>
                    <th className="px-3 py-2 font-medium">Телефон</th>
                    <th className="px-3 py-2 font-medium">Компания</th>
                    <th className="px-3 py-2 font-medium">Баркод</th>
                    <th className="px-3 py-2 font-medium">В БД</th>
                    <th className="px-3 py-2 font-medium">Заходили</th>
                    <th className="px-3 py-2 font-medium w-24" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {visitors.map((v) => {
                    const open = expandedId === v.userId;
                    const editing = editingId === v.userId;
                    const roles = v.roles?.length
                      ? v.roles
                      : (["visitor"] as Array<"visitor" | "exhibitor">);
                    const canManage = Boolean(v.manualTest);
                    const addedAt = v.addedAt || v.grantedAt;
                    return (
                      <React.Fragment key={v.userId}>
                        <tr
                          className={`align-top ${open ? "bg-indigo-50/40" : "hover:bg-gray-50"} cursor-pointer`}
                          onClick={() => setExpandedId(open ? null : v.userId)}
                        >
                          <td className="px-3 py-2.5">
                            <div className="text-[13px] text-gray-900">{v.fullName || "Без имени"}</div>
                            {v.position ? (
                              <div className="text-[11px] text-gray-400 truncate max-w-[180px]">{v.position}</div>
                            ) : null}
                            {canManage ? (
                              <span className="text-[10px] text-amber-700">тест</span>
                            ) : null}
                          </td>
                          <td className="px-3 py-2.5">
                            <div className="flex flex-wrap gap-1">
                              {roles.map((r) => (
                                <span
                                  key={r}
                                  className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                    r === "exhibitor"
                                      ? "bg-violet-50 text-violet-700"
                                      : "bg-sky-50 text-sky-700"
                                  }`}
                                >
                                  {roleLabel(r)}
                                </span>
                              ))}
                              {isGroupOnlyAccess(v) ? (
                                <span
                                  className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100"
                                  title="Доступ с другой выставки этой группы"
                                >
                                  Группа
                                </span>
                              ) : null}
                            </div>
                          </td>
                          <td className="px-3 py-2.5 text-[12px] text-gray-700 whitespace-nowrap">
                            {v.phone || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-[12px] text-gray-600 max-w-[140px] truncate">
                            {v.companyName || <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            {v.ticketNumber ? (
                              <div>
                                <code className="text-[11px] text-gray-800 break-all">{v.ticketNumber}</code>
                                <div className="text-[10px] text-gray-400">
                                  {v.barcodeOrigin === "generated" ? "тест" : "реплика"}
                                </div>
                              </div>
                            ) : isGroupOnlyAccess(v) ? (
                              <div title="Билет на другую выставку группы — баркод смотрите там">
                                <span className="inline-flex text-[10px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                                  Группа
                                </span>
                                <div className="text-[10px] text-gray-400 mt-0.5">другая выставка</div>
                              </div>
                            ) : (
                              <span className="text-[12px] text-gray-300">нет</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-[12px] text-gray-600 whitespace-nowrap">
                            {addedAt ? formatDate(addedAt) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            {v.authorized ? (
                              <span className="inline-flex items-center gap-1 text-[12px] text-emerald-700">
                                <CheckCircle2 size={13} /> Да
                                {v.lastSeenAt ? (
                                  <span className="text-[10px] text-gray-400 font-normal">
                                    {formatDate(v.lastSeenAt)}
                                  </span>
                                ) : null}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[12px] text-gray-400">
                                <CircleDashed size={13} /> Нет
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                            {canManage ? (
                              <div className="inline-flex">
                                <button
                                  type="button"
                                  title="Редактировать"
                                  aria-label="Редактировать"
                                  onClick={() => startEdit(v)}
                                  className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500"
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  type="button"
                                  title="Удалить"
                                  aria-label="Удалить"
                                  disabled={deletingId === v.userId}
                                  onClick={() => void handleDelete(v)}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-red-500 disabled:opacity-50"
                                >
                                  {deletingId === v.userId ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <Trash2 size={14} />
                                  )}
                                </button>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                        {editing ? (
                          <tr>
                            <td colSpan={8} className="px-3 pb-3">
                              <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3 space-y-2">
                                <div className="flex items-center justify-between">
                                  <p className="text-[12px] text-indigo-800 font-medium">Редактирование теста</p>
                                  <button
                                    type="button"
                                    onClick={() => setEditingId(null)}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                    aria-label="Закрыть"
                                  >
                                    <X size={14} />
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  <input
                                    value={editForm.fullName}
                                    onChange={(e) => setEditForm((f) => ({ ...f, fullName: e.target.value }))}
                                    placeholder="ФИО"
                                    className="px-3 py-2 rounded-lg border border-gray-200 text-[13px] bg-white outline-none focus:border-indigo-400"
                                  />
                                  <input
                                    value={editForm.position}
                                    onChange={(e) => setEditForm((f) => ({ ...f, position: e.target.value }))}
                                    placeholder="Должность"
                                    className="px-3 py-2 rounded-lg border border-gray-200 text-[13px] bg-white outline-none focus:border-indigo-400"
                                  />
                                  <input
                                    value={editForm.phone}
                                    onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                                    placeholder="Телефон"
                                    className="px-3 py-2 rounded-lg border border-gray-200 text-[13px] bg-white outline-none focus:border-indigo-400"
                                  />
                                  {roles.includes("exhibitor") || editForm.companyId ? (
                                    <select
                                      value={editForm.companyId}
                                      onChange={(e) =>
                                        setEditForm((f) => ({ ...f, companyId: e.target.value }))
                                      }
                                      className="px-3 py-2 rounded-lg border border-gray-200 text-[13px] bg-white outline-none focus:border-indigo-400"
                                    >
                                      {exhibitors.map((c) => (
                                        <option key={c.id} value={c.id}>
                                          {c.name}
                                        </option>
                                      ))}
                                    </select>
                                  ) : null}
                                </div>
                                <button
                                  type="button"
                                  disabled={savingEdit}
                                  onClick={() => void handleSaveEdit(v.userId)}
                                  className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-[12px] disabled:opacity-60 inline-flex items-center gap-2"
                                >
                                  {savingEdit ? <Loader2 size={14} className="animate-spin" /> : null}
                                  Сохранить
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : null}
                        {open && !editing ? (
                          <tr>
                            <td colSpan={8} className="px-3 pb-3">
                              <div className="space-y-3">
                                {id ? <VisitorAnalyticsPanel eventId={id} userId={v.userId} /> : null}
                                {v.ticketNumber || v.qrPayload ? (
                                  <VisitorCodes visitor={v} />
                                ) : isGroupOnlyAccess(v) ? (
                                  <p className="text-[12px] text-amber-800/80 px-1 py-2 rounded-lg bg-amber-50/80 border border-amber-100">
                                    Доступ по группе выставок: билет и баркод оформлены на другую выставку этой
                                    группы, на текущей — только доступ в приложение.
                                  </p>
                                ) : (
                                  <p className="text-[12px] text-gray-400 px-1 py-2">
                                    Нет билета / баркода. У синхронизированных посетителей код приходит из вашей БД;
                                    тестовый баркод генерируем только при ручной выдаче доступа.
                                  </p>
                                )}
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <AdminPagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}
